import { Download, Mic, Pause, Play } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface AudioPlayerCardProps {
  typeAudio?: 'management' | 'dashboard';
  onDownload?: () => void;
  title: string;
  audioUrl: string;
  isPlaying: boolean;
  onPlay: () => void;
  // Natural end of the track. onPlay is a TOGGLE in every parent, so using it
  // for 'ended' can race a card switch: a stale ended event from card A firing
  // after card B was started would toggle playback back to A. A dedicated
  // callback lets the parent clear only this card's own state. Optional - the
  // old toggle behaviour remains for parents that don't pass it.
  onEnded?: () => void;
}

const AudioPlayerCard = ({
  typeAudio = 'dashboard',
  title,
  audioUrl,
  isPlaying,
  onDownload,
  onPlay,
  onEnded,
}: AudioPlayerCardProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  // Set while the loadedmetadata/durationchange probe below is forcing a
  // seek to learn the real duration - 'timeupdate' fires during that seek
  // too, and must not be shown as real playback progress.
  const probingDurationRef = useRef(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);

  // Play / Pause
  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    if (isPlaying) {
      audio.play().catch((err) => {
        console.error(err);
      });
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  // Update duration
  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    // Reads `duration` if the element already knows it, without touching
    // currentTime - safe to call at any time, including synchronously at
    // mount before the element has even started loading (duration reads
    // NaN then, not Infinity, so it's simply a no-op).
    const readKnownDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setTotalDuration(audio.duration);
        if (probingDurationRef.current) {
          probingDurationRef.current = false;
          audio.currentTime = 0;
        }
        return true;
      }
      return false;
    };

    // Chrome (and others) never reports a real `duration` for a streamed
    // Ogg/Opus source on its own - 'loadedmetadata' reads Infinity/NaN, and
    // no later 'durationchange' arrives with the true value either, because
    // the browser only learns it by demuxing the file's last page (the
    // granule position), which it has no reason to fetch unless playback
    // actually reaches the end. The standard workaround is to force that:
    // seek near the end once metadata loads, which makes the browser
    // resolve the real duration and fire 'durationchange' with it, then
    // seek back to 0 (handled by readKnownDuration above). This must only
    // run from an event that guarantees metadata is actually loaded - doing
    // it before then made the element seek to a bogus starting position
    // once loading did complete.
    const handleDurationProbe = () => {
      if (readKnownDuration()) return;
      if (!probingDurationRef.current) {
        probingDurationRef.current = true;
        audio.currentTime = Number.MAX_SAFE_INTEGER;
      }
    };

    const handleTimeUpdate = () => {
      if (probingDurationRef.current) return;
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setCurrentTime(0);
      (onEnded ?? onPlay)();
    };

    audio.addEventListener('loadedmetadata', handleDurationProbe);
    audio.addEventListener('durationchange', handleDurationProbe);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    // A cached response can finish loading metadata before this effect gets
    // a chance to attach the listeners above - the events fire and are
    // missed entirely, leaving totalDuration stuck at its initial 0 despite
    // the element already knowing the real duration by the time this runs.
    // Only reads what's already resolved - never starts the seek probe,
    // since metadata may just as easily not be loaded yet at this exact
    // synchronous point even for a cached response.
    readKnownDuration();

    return () => {
      audio.removeEventListener('loadedmetadata', handleDurationProbe);
      audio.removeEventListener('durationchange', handleDurationProbe);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onPlay, onEnded]);

  const handlePlay = () => {
    onPlay();
  };

  const formatTime = (time: number) => {
    // Infinity, not just NaN: a server response with no Content-Length (or a
    // Range request still in flight) reads as an unknown/unbounded duration
    // in some browsers until more of the file has loaded.
    if (Number.isNaN(time) || !Number.isFinite(time)) return '0:00';

    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const bars = [
    24, 12, 18, 16, 36, 32, 14, 22, 16, 22, 32, 34, 18, 16, 38, 22, 16, 14, 14, 14, 14, 14, 36, 18,
    34, 22, 18, 18, 12, 20, 40,
  ];

  return (
    <>
      {typeAudio === 'dashboard' ? (
        <div
          className={cn(
            'flex flex-wrap flex-1 items-center gap-5 rounded-2xl  p-5 transition-all justify-between bg-db-card',
            isPlaying ? 'border-blue-500 bg-blue-50 ' : 'border '
          )}
        >
          {/* biome-ignore lint/a11y/useMediaCaption: audio player does not require captions */}
          <audio ref={audioRef} src={audioUrl} preload="metadata" />
          <div className="flex flex-1 flex-row items-center line-clamp-1 gap-3">
            <button
              type="button"
              onClick={handlePlay}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-full transition',
                isPlaying ? 'bg-blue-600 text-white' : 'bg-gray-500 text-white'
              )}
            >
              {isPlaying ? <Pause /> : <Play fill="white" />}
            </button>
            <div className="flex-1 line-clamp-2 text-ellipsis">
              <span
                className={cn(
                  'mb-4 title2 w-[80%] text-nowrap overflow-hidden',
                  isPlaying ? 'text-text-blue-100' : 'text-text-grey'
                )}
              >
                {title}
              </span>

              <div className="flex w-full items-center gap-1">
                {bars.map((height, index) => (
                  <div
                    key={index}
                    className={cn(
                      'min-w-0 flex-1 rounded-full transition-all',
                      isPlaying ? 'bg-text-blue-100' : 'bg-gray-500'
                    )}
                    style={{ height }}
                  />
                ))}
              </div>
            </div>

            <span
              className={cn(
                ' title1 flex flex-row gap-1',
                isPlaying ? 'text-text-blue-100' : 'text-text-grey'
              )}
            >
              {formatTime(currentTime)} / {formatTime(totalDuration)}
              <Mic size={30} className={cn(isPlaying ? 'text-text-blue-100' : 'text-text-grey')} />
            </span>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'flex flex-wrap transition-all gap-3 bg-background border rounded-[12px] min-w-[300px] p-3 flex flex-row items-center',
            isPlaying ? 'border-blue-500 bg-blue-50 ' : 'border '
          )}
        >
          {/* biome-ignore lint/a11y/useMediaCaption: audio player does not require captions */}
          <audio ref={audioRef} src={audioUrl} preload="metadata" />
          <div className="">
            {/* A visible play/pause control, not the mic glyph this used to
                be - the card was playable but nothing about it said so. */}
            <button
              type="button"
              onClick={handlePlay}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-full transition',
                isPlaying ? 'bg-blue-600 text-white' : 'bg-gray-500 text-white'
              )}
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} fill="white" />}
            </button>
          </div>

          <div className="flex-1">
            <span
              className={cn(
                'mb-4 title3medium ',
                isPlaying ? 'text-text-blue-100' : 'text-text-grey'
              )}
            >
              {title}
            </span>
          </div>

          <span className={cn('title2 mr-2', isPlaying ? 'text-text-blue-100' : 'text-text-grey')}>
            {formatTime(currentTime)} / {formatTime(totalDuration)}
          </span>

          <span
            className={cn(
              'mr-5 title1 flex flex-row gap-1',
              isPlaying ? 'text-text-blue-100' : 'text-text-grey'
            )}
          >
            <button onClick={onDownload} type="button">
              <Download />
            </button>
          </span>
        </div>
      )}
    </>
  );
};

export default AudioPlayerCard;
